import { HttpRequest } from '@angular/common/http';
import { Injectable, Provider, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { OutboxEntry } from 'src/app/core/offline/offline.models';
import {
  ConflictField,
  HandlerContext,
  OUTBOX_HANDLERS,
  OutboxHandler,
} from 'src/app/core/offline/outbox-handler';
import { environment } from 'src/environments/environment';
import {
  ReportCreatePayload,
  ReportFields,
  ReportPhotoPayload,
  ReportUpdatePayload,
} from './report-offline.models';

// Offline E2 step 2: the Baujournal handlers of the offline core (spec §4.1). Provided in the AppModule, so the sync
// finds them on every page (plan A9 of step 1).

const DATE_FORMAT = new Intl.DateTimeFormat('de-CH', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

/** fields of the conflict dialog and of «Selbst zusammenführen», in form order */
export const REPORT_CONFLICT_KEYS = [
  'date',
  'timeFrom',
  'timeTo',
  'title',
  'weather',
  'attendees',
  'text',
] as const;
export type ReportConflictKey = (typeof REPORT_CONFLICT_KEYS)[number];

export function reportsUrl(journalId: string): string {
  return `${environment.apiUrl}/construction-journals/${encodeURIComponent(journalId)}/reports`;
}

export function reportUrl(journalId: string, reportId: string): string {
  return `${reportsUrl(journalId)}/${encodeURIComponent(reportId)}`;
}

/** dd.MM.yyyy of an ISO string (server) or a Date (the date picker stores one when the date is changed offline) */
export function formatReportDate(value: unknown): string {
  if (!(value instanceof Date) && (typeof value !== 'string' || !value)) return '';
  const date = value instanceof Date ? value : new Date(value as string);
  return Number.isNaN(date.getTime()) ? '' : DATE_FORMAT.format(date);
}

export function reportFieldText(
  key: ReportConflictKey,
  value: unknown,
): string {
  if (value === null || value === undefined || value === '') return '–';
  if (key === 'date') return formatReportDate(value) || '–';
  if (key === 'attendees')
    return Array.isArray(value) && value.length > 0 ? value.join(', ') : '–';
  return String(value);
}

/** «Tagesrapport, 02.10.2026», or only one of both */
export function reportLabel(fields: ReportFields | undefined): string {
  return [fields?.title?.trim(), formatReportDate(fields?.date)]
    .filter(Boolean)
    .join(', ');
}

/** «Kopieren»: the report as text, so a refused change is not lost (spec §6) */
export function reportCopyText(
  fields: ReportFields | undefined,
  translate: TranslateService,
): string {
  const head = (
    ['date', 'timeFrom', 'timeTo', 'title', 'weather', 'attendees'] as const
  )
    .filter((key) => fields?.[key] !== undefined)
    .map(
      (key) =>
        `${translate.instant(`offline.report.fields.${key}`)}: ${reportFieldText(key, fields?.[key])}`,
    );
  return [...head, '', fields?.text ?? ''].join('\n').trim();
}

/** my changed fields against the newer server version (conflict dialog, «Selbst zusammenführen») */
export function reportConflictFields(
  mine: ReportFields,
  current: Record<string, unknown>,
  translate: TranslateService,
): ConflictField[] {
  return REPORT_CONFLICT_KEYS.filter((key) => key in mine).map((key) => ({
    key,
    label: translate.instant(`offline.report.fields.${key}`),
    mine: reportFieldText(key, mine[key]),
    theirs: reportFieldText(key, current[key]),
  }));
}

function describeReport(
  translate: TranslateService,
  typeKey: string,
  label: string,
): string {
  const type = translate.instant(typeKey);
  return label ? `${type} – ${label}` : type;
}

@Injectable()
export class ReportCreateHandler implements OutboxHandler {
  readonly type = 'report.create' as const;
  readonly creates = true;
  private readonly translate = inject(TranslateService);

  async build(entry: OutboxEntry): Promise<HttpRequest<unknown>> {
    const { journalId, fields } =
      entry.payload as unknown as ReportCreatePayload;
    return new HttpRequest('POST', reportsUrl(journalId), fields);
  }

  describe(entry: OutboxEntry): string {
    return describeReport(
      this.translate,
      'offline.types.report.create',
      reportLabel((entry.payload as unknown as ReportCreatePayload).fields),
    );
  }

  copyText(entry: OutboxEntry): string {
    return reportCopyText(
      (entry.payload as unknown as ReportCreatePayload).fields,
      this.translate,
    );
  }
}

@Injectable()
export class ReportUpdateHandler implements OutboxHandler {
  readonly type = 'report.update' as const;
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);

  async build(entry: OutboxEntry): Promise<HttpRequest<unknown>> {
    const { journalId, reportId, ifVersion, fields } =
      entry.payload as unknown as ReportUpdatePayload;
    return new HttpRequest(
      'PUT',
      reportUrl(journalId, reportId),
      ifVersion === undefined ? fields : { ...fields, ifVersion },
    );
  }

  describe(entry: OutboxEntry): string {
    const payload = entry.payload as unknown as ReportUpdatePayload;
    return describeReport(
      this.translate,
      'offline.types.report.update',
      payload.label ?? reportLabel(payload.fields),
    );
  }

  /** the label first, so the copy says which report it belongs to (the fields are only the changed ones) */
  copyText(entry: OutboxEntry): string {
    const payload = entry.payload as unknown as ReportUpdatePayload;
    const label = payload.label ?? reportLabel(payload.fields);
    return [label, reportCopyText(payload.fields, this.translate)]
      .filter(Boolean)
      .join('\n\n');
  }

  conflictFields(
    entry: OutboxEntry,
    current: Record<string, unknown>,
  ): ConflictField[] {
    return reportConflictFields(
      (entry.payload as unknown as ReportUpdatePayload).fields,
      current,
      this.translate,
    );
  }

  /** the stored payload may still carry local:… (replaced only in the sent copy): the server id comes from `current` */
  openMerge(entry: OutboxEntry, current: Record<string, unknown>): void {
    const { journalId } = entry.payload as unknown as ReportUpdatePayload;
    const reportId =
      typeof current['id'] === 'string'
        ? current['id']
        : entry.entityRef.serverId;
    if (!reportId) return;
    void this.router.navigate(
      [
        '/projects',
        entry.entityRef.projectId,
        'construction-journals',
        journalId,
        'reports',
        reportId,
      ],
      {
        queryParams: { merge: entry.opId },
      },
    );
  }
}

@Injectable()
export class ReportPhotoHandler implements OutboxHandler {
  readonly type = 'report.photo' as const;
  private readonly translate = inject(TranslateService);

  async build(
    entry: OutboxEntry,
    ctx: HandlerContext,
  ): Promise<HttpRequest<unknown>> {
    const { journalId, reportId, fileName, blobIds } =
      entry.payload as unknown as ReportPhotoPayload;
    const blob = blobIds?.[0] ? await ctx.getBlob(blobIds[0]) : undefined;
    if (!blob) throw new Error('report.photo without its file');
    const form = new FormData();
    // same field, name and type as the online upload: an interrupted upload keeps its multipart hash (Task 9)
    form.append(
      'files',
      new File([blob], fileName, { type: blob.type }),
      fileName,
    );
    return new HttpRequest(
      'PUT',
      `${reportUrl(journalId, reportId)}/pictures`,
      form,
    );
  }

  describe(entry: OutboxEntry): string {
    return describeReport(
      this.translate,
      'offline.types.report.photo',
      (entry.payload as unknown as ReportPhotoPayload).label ?? '',
    );
  }
}

export const REPORT_OUTBOX_HANDLERS: Provider[] = [
  { provide: OUTBOX_HANDLERS, useClass: ReportCreateHandler, multi: true },
  { provide: OUTBOX_HANDLERS, useClass: ReportUpdateHandler, multi: true },
  { provide: OUTBOX_HANDLERS, useClass: ReportPhotoHandler, multi: true },
];
